import type React from 'react'
import { useCallback, useMemo, useState } from 'react'

export type PageImageRecovery = {
  /** The Rolodex URL to render: undefined once the source is exhausted or while a manual retry is re-signing it. */
  url: string | undefined
  /** The source has been given up on: it errored again after a re-sign, or the re-sign handed back the same URL. */
  exhausted: boolean
  /** A manual retry's re-sign is in flight; the URL is withheld so its result is fetched fresh. */
  retrying: boolean
  onLoad: (src: string) => void
  onError: (src: string) => void
  retry: () => void
}

type UsePageImageRecoveryArgs = {
  resetKey: string
  url: string | undefined
  forceRefresh: () => Promise<void>
  onRetry?: () => void
}

type ResignAttempt = {
  key: string
  src: string | undefined
  fromRetry: boolean
  settled: boolean
}

/** The src an `<img>` was rendered with, so it compares equal to the URL React was given. */
export function imageElementSrc(element: HTMLImageElement): string {
  return element.getAttribute('src') ?? ''
}

export function imageEventSrc(
  event: React.SyntheticEvent<HTMLImageElement>
): string {
  return imageElementSrc(event.currentTarget)
}

function isRetrying(attempt: ResignAttempt | null, resetKey: string): boolean {
  return (
    attempt !== null &&
    attempt.key === resetKey &&
    attempt.fromRetry &&
    !attempt.settled
  )
}

function isExhaustedBy(
  attempt: ResignAttempt | null,
  resetKey: string,
  url: string | undefined
): boolean {
  return (
    attempt !== null &&
    attempt.key === resetKey &&
    attempt.settled &&
    url === attempt.src
  )
}

/**
 * Re-signs a Rolodex page-image URL once after its `<img>` errors, because an
 * expired signed URL fails exactly like a missing file. A second error on the
 * same image, or a re-sign that hands back the same URL (which the browser
 * will not refetch), marks it exhausted so the caller falls through to
 * PDF.js. Events for any other src (a PDF.js blob, a sibling fallback) are
 * ignored, so a fallback that loads cannot re-arm the Rolodex source.
 */
export function usePageImageRecovery({
  resetKey,
  url,
  forceRefresh,
  onRetry,
}: UsePageImageRecoveryArgs): PageImageRecovery {
  const [attempt, setAttempt] = useState<ResignAttempt | null>(null)
  const [erroredTwiceKey, setErroredTwiceKey] = useState<string | null>(null)

  const resign = useCallback(
    (next: ResignAttempt) => {
      setAttempt(next)
      const settle = () =>
        setAttempt((current) =>
          current?.key === next.key && current.src === next.src
            ? { ...current, settled: true }
            : current
        )
      void forceRefresh().then(settle, settle)
    },
    [forceRefresh]
  )

  const onError = useCallback(
    (src: string) => {
      if (!url || src !== url) return
      if (attempt?.key === resetKey) {
        setErroredTwiceKey(resetKey)
        return
      }
      onRetry?.()
      resign({ key: resetKey, src, fromRetry: false, settled: false })
    },
    [attempt, onRetry, resetKey, resign, url]
  )

  const onLoad = useCallback(
    (src: string) => {
      if (!url || src !== url) return
      setAttempt(null)
      setErroredTwiceKey(null)
    },
    [url]
  )

  const retry = useCallback(() => {
    setErroredTwiceKey(null)
    resign({ key: resetKey, src: url, fromRetry: true, settled: false })
  }, [resetKey, resign, url])

  const exhausted =
    erroredTwiceKey === resetKey || isExhaustedBy(attempt, resetKey, url)
  const retrying = isRetrying(attempt, resetKey)
  const usableUrl = exhausted || retrying ? undefined : url

  return useMemo(
    () => ({ url: usableUrl, exhausted, retrying, onError, onLoad, retry }),
    [usableUrl, exhausted, retrying, onError, onLoad, retry]
  )
}
